import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  ShieldCheck,
  Stethoscope,
  Video,
  MapPin,
  Home,
  Users,
  Globe,
  CheckCircle2,
  X,
  Sparkles,
} from 'lucide-react';
import { Button } from '@medikei/ui';
import { filesApi } from '@/api';

export interface DoctorProfilePreviewData {
  displayName?: string | null;
  name?: string | null;
  crm?: string | null;
  crmState?: string | null;
  tagline?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
  coverUrl?: string | null;
  specialty?: string | null;
  specialties?: Array<{
    name?: string;
    specialty?: { name?: string };
    primary?: boolean;
    status?: string;
  }>;
  languages?: string[];
  awards?: string[];
  publications?: string | null;
  minAge?: number | null;
  maxAge?: number | null;
  allowedAgeGroups?: string[];
  hasTelemedicine?: boolean;
  hasInPerson?: boolean;
  hasHomeCare?: boolean;
  clinicName?: string | null;
  clinicAddress?: string | null;
  clinicCity?: string | null;
  clinicState?: string | null;
  homeCareRadiusKm?: number | null;
}

interface DoctorProfilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  data: DoctorProfilePreviewData | null;
  isAdminView?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  isApproving?: boolean;
}

export function DoctorProfilePreviewModal({
  open,
  onClose,
  data,
  isAdminView = false,
  onApprove,
  onReject,
  isApproving = false,
}: DoctorProfilePreviewModalProps) {
  if (!data) return null;

  const fullName =
    data.displayName || data.name || 'Dr(a). Profissional de Saúde';
  const crmFormatted = data.crm
    ? `CRM ${data.crm}/${data.crmState || 'SP'}`
    : 'CRM Registrado';

  const primarySpecialtyName =
    data.specialty ||
    data.specialties?.find((s) => s.primary)?.name ||
    data.specialties?.find((s) => s.primary)?.specialty?.name ||
    'Clínica Geral';

  const secondarySpecialties = (data.specialties || [])
    .filter((s) => !s.primary)
    .map((s) => s.name || s.specialty?.name)
    .filter(Boolean);

  const languagesList =
    data.languages && data.languages.length > 0
      ? data.languages
      : ['Português'];

  const ageMin = data.minAge ?? 0;
  const ageMax = data.maxAge ?? 120;

  const [coverError, setCoverError] = useState(false);
  const [photoError, setPhotoError] = useState(false);

  const resolveImageUrl = (path?: string | null): string | null => {
    if (!path) return null;
    if (
      path.startsWith('http://') ||
      path.startsWith('https://') ||
      path.startsWith('blob:') ||
      path.startsWith('data:')
    ) {
      return path;
    }
    return filesApi.getFileUrl(path);
  };

  const resolvedCover = resolveImageUrl(data.coverUrl);
  const resolvedPhoto = resolveImageUrl(data.photoUrl);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
        {/* Top Header Bar */}
        <div className="px-6 py-4 bg-muted/30 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 text-primary rounded-lg">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-foreground">
                {isAdminView
                  ? 'Auditoria & Pré-visualização do Perfil Médico'
                  : 'Pré-visualização do seu Perfil Público'}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                Exibição de alta fidelidade como os pacientes visualizam na plataforma
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Profile Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Panoramic Cover */}
          <div className="relative w-full h-44 sm:h-52 bg-gradient-to-r from-blue-700 via-primary to-indigo-800 overflow-hidden">
            {resolvedCover && !coverError ? (
              <img
                src={resolvedCover}
                alt="Capa do perfil"
                className="w-full h-full object-cover"
                onError={() => setCoverError(true)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/25">
                <Stethoscope className="w-20 h-20 opacity-30" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>

          {/* Profile Header Card */}
          <div className="px-6 sm:px-8 -mt-16 sm:-mt-18 relative z-10 pb-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div className="flex items-end gap-4">
                {/* Avatar with Ring */}
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-3xl bg-muted border-4 border-card shadow-xl overflow-hidden shrink-0 relative bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center text-white text-3xl font-black">
                  {resolvedPhoto && !photoError ? (
                    <img
                      src={resolvedPhoto}
                      alt={fullName}
                      className="w-full h-full object-cover"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <span>{fullName.charAt(0).toUpperCase()}</span>
                  )}
                </div>

                <div className="mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-black text-foreground">
                      {fullName}
                    </h1>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3" /> CFM Ativo
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-primary mt-0.5">
                    {primarySpecialtyName} • {crmFormatted}
                  </p>
                </div>
              </div>

              {/* Verified Badge */}
              <div className="sm:self-end">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted/60 border border-border text-xs font-semibold text-muted-foreground">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span>Médico Verificado Medikei</span>
                </span>
              </div>
            </div>

            {/* Tagline */}
            {data.tagline && (
              <p className="text-sm font-medium text-foreground/80 italic border-l-2 border-primary pl-3 py-0.5">
                "{data.tagline}"
              </p>
            )}

            {/* Specialty Badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary text-primary-foreground shadow-2xs">
                {primarySpecialtyName} (Principal)
              </span>
              {secondarySpecialties.map((spec, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full text-xs font-semibold bg-muted text-foreground border border-border"
                >
                  {spec}
                </span>
              ))}
            </div>

            {/* Bio Section */}
            {data.bio && (
              <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-1.5">
                <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Sobre o Especialista
                </h4>
                <p className="text-xs sm:text-sm text-foreground leading-relaxed whitespace-pre-line">
                  {data.bio}
                </p>
              </div>
            )}

            {/* Modalidades de Atendimento */}
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Modalidades de Consulta Habilitadas
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div
                  className={`p-3.5 rounded-2xl border flex flex-col gap-1 transition-all ${
                    data.hasTelemedicine !== false
                      ? 'bg-blue-500/10 border-blue-500/25 text-blue-900 dark:text-blue-200'
                      : 'bg-muted/30 border-border text-muted-foreground opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2 font-bold text-xs">
                    <Video className="w-4 h-4 text-blue-600" />
                    <span>Telemedicina</span>
                  </div>
                  <span className="text-[11px] opacity-80">
                    {data.hasTelemedicine !== false
                      ? 'Consultas por vídeo ao vivo em todo o Brasil'
                      : 'Não habilitado'}
                  </span>
                </div>

                <div
                  className={`p-3.5 rounded-2xl border flex flex-col gap-1 transition-all ${
                    data.hasInPerson
                      ? 'bg-indigo-500/10 border-indigo-500/25 text-indigo-900 dark:text-indigo-200'
                      : 'bg-muted/30 border-border text-muted-foreground opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2 font-bold text-xs">
                    <MapPin className="w-4 h-4 text-indigo-600" />
                    <span>Consultório Físico</span>
                  </div>
                  <span className="text-[11px] opacity-80">
                    {data.hasInPerson
                      ? `${data.clinicName || 'Clínica'}, ${data.clinicCity || ''}`
                      : 'Atendimento presencial inativo'}
                  </span>
                </div>

                <div
                  className={`p-3.5 rounded-2xl border flex flex-col gap-1 transition-all ${
                    data.hasHomeCare
                      ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-900 dark:text-emerald-200'
                      : 'bg-muted/30 border-border text-muted-foreground opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2 font-bold text-xs">
                    <Home className="w-4 h-4 text-emerald-600" />
                    <span>Médico em Casa</span>
                  </div>
                  <span className="text-[11px] opacity-80">
                    {data.hasHomeCare
                      ? `Raio de cobertura: até ${data.homeCareRadiusKm || 15} km`
                      : 'Visitas domiciliares inativas'}
                  </span>
                </div>
              </div>
            </div>

            {/* Faixa Etária e Idiomas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
              <div className="space-y-1">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-primary" />
                  <span>Público Atendido</span>
                </span>
                <p className="text-xs sm:text-sm font-semibold text-foreground">
                  Pacientes de {ageMin} a {ageMax} anos
                </p>
                <div className="flex flex-wrap gap-1 pt-1">
                  {(data.allowedAgeGroups && data.allowedAgeGroups.length > 0
                    ? data.allowedAgeGroups
                    : ['Todas as Idades']
                  ).map((grp, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-muted text-muted-foreground border border-border"
                    >
                      {grp}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-primary" />
                  <span>Idiomas de Consulta</span>
                </span>
                <div className="flex flex-wrap gap-1 pt-1">
                  {languagesList.map((lang, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions (Com botões de ação caso seja visão do Admin) */}
        <div className="p-4 bg-muted/20 border-t border-border flex items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-muted-foreground hidden sm:block">
            {isAdminView
              ? 'Verifique a foto de perfil, foto de capa e informações cadastrais antes de aprovar.'
              : 'Você pode atualizar suas fotos e bio a qualquer momento no menu "Editar Perfil".'}
          </p>

          <div className="flex items-center gap-2 ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="rounded-xl text-xs font-semibold"
            >
              Fechar
            </Button>

            {isAdminView && onReject && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onReject}
                className="rounded-xl text-xs font-bold text-rose-600 border-rose-300 hover:bg-rose-50"
              >
                Rejeitar Cadastro
              </Button>
            )}

            {isAdminView && onApprove && (
              <Button
                type="button"
                size="sm"
                onClick={onApprove}
                disabled={isApproving}
                className="rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
              >
                {isApproving ? 'Aprovando...' : 'Aprovar Médico'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
